// The transport: the only hand-written code in this package.
//
// It knows how to send *an* operation — build the URL, serialise the query, set the headers, parse
// the reply, raise the right error. It knows nothing about any particular endpoint; that is all
// generated from the contract into `src/generated/`.
//
// Spec: spec/08-api-design.md §2.2 (requests and headers), §2.3 (success codes), §2.6 (problem+json).
import { ApiUsageError, toApiError } from "./problem.js";
function appendQuery(search, name, value) {
    if (value === undefined || value === null)
        return;
    if (Array.isArray(value)) {
        for (const item of value)
            search.append(name, String(item));
        return;
    }
    search.append(name, String(value));
}
function buildPath(spec, values) {
    return spec.path.replaceAll(/\{([^}]+)\}/g, (_match, name) => {
        const value = values?.[name];
        if (value === undefined) {
            throw new ApiUsageError(`${spec.operationId}: the path parameter "${name}" is required.`);
        }
        return encodeURIComponent(String(value));
    });
}
function buildUrl(baseUrl, spec, input) {
    const origin = baseUrl.replace(/\/+$/, '');
    const url = `${origin}${buildPath(spec, input.path)}`;
    const query = input.query;
    if (query === undefined)
        return url;
    const search = new URLSearchParams();
    for (const [name, value] of Object.entries(query)) {
        // The contract rejects unknown query parameters with a `400` rather than ignoring them
        // (spec/08 §2.4). Catching the typo here names the parameter and the operation; catching it at
        // the server names neither, three hundred milliseconds later.
        if (!spec.queryParams.includes(name)) {
            throw new ApiUsageError(`${spec.operationId}: "${name}" is not a query parameter of ${spec.method} ${spec.path}. ` +
                `It accepts: ${spec.queryParams.join(', ') || 'none'}.`);
        }
        appendQuery(search, name, value);
    }
    const serialised = search.toString();
    return serialised === '' ? url : `${url}?${serialised}`;
}
async function resolveToken(token) {
    return typeof token === 'function' ? await token() : token;
}
/** The transport a generated client is bound to. `createApiClient` in `src/client.ts` wraps it. */
export function createTransport(options) {
    const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    return {
        async request(spec, input = {}) {
            const url = buildUrl(options.baseUrl, spec, input);
            const headers = new Headers({ accept: 'application/json, application/problem+json' });
            for (const [name, value] of Object.entries(options.headers ?? {}))
                headers.set(name, value);
            const token = await resolveToken(options.token);
            if (token !== undefined && token !== '')
                headers.set('authorization', `Bearer ${token}`);
            const acceptLanguage = input.acceptLanguage ?? options.acceptLanguage;
            if (acceptLanguage !== undefined)
                headers.set('accept-language', acceptLanguage);
            if (input.idempotencyKey !== undefined)
                headers.set('idempotency-key', input.idempotencyKey);
            if (input.ifMatch !== undefined)
                headers.set('if-match', input.ifMatch);
            for (const [name, value] of Object.entries(input.headerParams ?? {})) {
                if (!spec.headerParams.includes(name)) {
                    throw new ApiUsageError(`${spec.operationId}: "${name}" is not a header parameter of this operation.`);
                }
                headers.set(name, value);
            }
            for (const [name, value] of Object.entries(input.headers ?? {}))
                headers.set(name, value);
            // better-auth refuses any write to `/v1/auth/*` that is not `application/json` (415), even one
            // that takes no body, such as sign-out. Those go out with an empty JSON object.
            const body = input.body ?? (spec.method !== 'GET' && spec.path.startsWith('/v1/auth/') ? {} : undefined);
            const hasBody = body !== undefined;
            if (hasBody)
                headers.set('content-type', 'application/json');
            const response = await doFetch(url, {
                method: spec.method,
                headers,
                ...(hasBody ? { body: JSON.stringify(body) } : {}),
                ...(input.signal === undefined ? {} : { signal: input.signal }),
                ...(options.credentials === undefined ? {} : { credentials: options.credentials }),
            });
            const requestId = response.headers.get('x-request-id') ?? undefined;
            if (requestId !== undefined)
                options.onRequestId?.(requestId, spec.operationId);
            input.onResponse?.({ status: response.status, headers: response.headers });
            if (!response.ok)
                throw await toApiError(response, requestId);
            // `204`, and any success the contract declares without a body (spec/08 §2.3).
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return undefined;
            }
            // A text body — an audit export's `text/csv` — is the string itself; the generated result
            // type says `string` for every success the contract declares as text.
            if (response.headers.get('content-type')?.trimStart().toLowerCase().startsWith('text/') === true) {
                return (await response.text());
            }
            return (await response.json());
        },
    };
}
//# sourceMappingURL=transport.js.map